import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '@/services/api';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    
    // Listen to Supabase auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        handleSupabaseAuth(session);
      } else if (event === 'SIGNED_OUT') {
        handleLogout();
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const userData = await api.auth.getMe();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('token');
        setUser(null);
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const handleSupabaseAuth = async (session) => {
    try {
      // Exchange Supabase token for App Token via backend
      const token = session.access_token;
      
      // We need to check if this is a Google Login or just a session restore
      // For now, we try to exchange it. If it fails (e.g. user not in DB), we handle error.
      const data = await api.auth.googleLogin(token);
      
      if (data.token) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth exchange error:', error);
      // If exchange fails, we should probably sign out from Supabase to avoid loop
      // But be careful not to loop if the error is persistent
      if (error.response?.status === 404) {
          // User not found in our DB
          alert("Your Google account is not registered with us. Please contact admin.");
          await supabase.auth.signOut();
      }
    }
  };

  const login = async (email, password) => {
    try {
      const data = await api.auth.login({ email, password });
      if (data.token) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setIsAuthenticated(true);
        return data;
      }
      return data;
    } catch (error) {
      console.error('Login error in AuthContext:', error);
      // Re-throw the error so the Login component can handle it
      throw error;
    }
  };

  const requestMagicLink = async (email) => {
    return await api.auth.requestMagicLink({ email });
  };

  const register = async (userData) => {
    const data = await api.auth.register(userData);
    localStorage.setItem('token', data.token);
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    handleLogout();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        requestMagicLink,
        register,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
